/**
 * Lazy imports that survive a deploy happening underneath them.
 *
 * Vite names every chunk by content hash, and Cloudflare Pages serves one deployment's assets at
 * a time. So the moment a new build goes live, `/assets/ZipImport-Bzrj-_jt.js` stops existing —
 * and any plugin session still running the previous build asks for exactly that filename the
 * next time someone opens the File tab. The import rejects, nothing catches it, and the tab is
 * dead until the user works out that reopening the plugin fixes it.
 *
 * This is not theoretical and it is not rare. It was captured in production within an hour of
 * the error table existing:
 *
 *   build_id 2026-10-14 13:50 UTC, errors at 14:46
 *     Failed to fetch dynamically imported module: /assets/ZipImport-Bzrj-_jt.js
 *     Failed to fetch dynamically imported module: /assets/StorybookImport-DYNrU78u.js
 *
 * Six occurrences, two tabs, one deploy. It will happen on every deploy to anyone with the panel
 * open, which — for a plugin people leave open while they work — is most of them.
 *
 * THE ONLY REPAIR IS A RELOAD. The file is genuinely gone; no retry of the same URL can succeed,
 * and there is no in-page way to learn the new hash short of re-fetching the document. Reloading
 * fetches a fresh index.html, which names the new chunks.
 *
 * WHICH MAKES THE LOOP GUARD THE IMPORTANT PART. Reload-on-import-failure with no guard turns a
 * persistent failure — offline, a chunk that 500s, a genuine error inside the module — into an
 * infinite refresh, which is worse than the broken tab it replaces: the user cannot even read
 * the error. So:
 *
 *   - only stale-chunk-SHAPED failures reload; anything else rethrows untouched
 *   - at most one reload per RELOAD_WINDOW_MS, recorded in sessionStorage
 *   - offline never reloads, because the reload cannot succeed either
 *   - a second failure after reloading rethrows, so the user sees a real error and we log it
 *
 * The error is reported BEFORE reloading, and the report is flushed, because a reload cancels
 * in-flight requests. Losing the signal would mean fixing this bug and never learning whether it
 * recurred.
 */

import { getLogger, flushErrors } from '../helpers/logger';

const log = getLogger('ChunkRecovery');

/** One reload per window. Long enough that a reload loop cannot form, short enough to re-arm. */
const RELOAD_WINDOW_MS = 30_000;
const RELOAD_KEY = 'c2d:chunk-reload-at';

/** Wait this long for the error report to leave before reloading. */
const FLUSH_BUDGET_MS = 600;

/**
 * Is this the "the chunk is gone" failure, or a real error inside the module?
 *
 * Browsers disagree on the wording, and getting this wrong in either direction is bad: too
 * narrow and the deploy case stays broken, too broad and a genuine module error triggers
 * reloads. Matching the three known phrasings is deliberate — an unrecognised failure rethrows,
 * which is the safe default.
 */
export function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /failed to fetch dynamically imported module/i.test(message) || // Chromium
    /error loading dynamically imported module/i.test(message) || // Firefox
    /importing a module script failed/i.test(message) // Safari
  );
}

/** sessionStorage throws in some privacy modes; never let bookkeeping break the recovery. */
function reloadedRecently(): boolean {
  try {
    const at = Number(sessionStorage.getItem(RELOAD_KEY));
    return Number.isFinite(at) && at > 0 && Date.now() - at < RELOAD_WINDOW_MS;
  } catch {
    // Cannot tell, so assume we have — refusing to reload degrades to today's behaviour, while
    // guessing the other way risks the loop this guard exists to prevent.
    return true;
  }
}

function markReloaded(): void {
  try {
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    /* see above: unreadable storage means we will not reload again anyway */
  }
}

/**
 * Decide what to do about a failed chunk load. Exported for the tests, which cannot reload a page.
 *
 * Returns whether a reload should happen; the caller performs it. Keeping the decision pure is
 * what makes the loop guard testable at all.
 */
export function shouldReloadFor(error: unknown, online = true): boolean {
  if (!isStaleChunkError(error)) return false;
  if (!online) return false;
  if (reloadedRecently()) return false;
  return true;
}

/**
 * `lazy()`'s factory, wrapped so a deploy mid-session repairs itself.
 *
 * `name` is only for the log line — it is what tells you WHICH tab broke, which the stack trace
 * of a failed dynamic import does not.
 */
export function withChunkRecovery<T>(name: string, factory: () => Promise<T>): () => Promise<T> {
  return async () => {
    try {
      return await factory();
    } catch (error) {
      const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;

      if (!shouldReloadFor(error, online)) {
        // Either a real error, or we already reloaded and it did not help. Let it through: the
        // global handler reports it and the user sees a failure instead of a refresh loop.
        log.error(`Chunk "${name}" failed and will not be retried`, {
          message: error instanceof Error ? error.message : String(error),
          stale: isStaleChunkError(error),
          online,
          alreadyReloaded: reloadedRecently(),
        });
        throw error;
      }

      log.error(`Chunk "${name}" is stale — a deploy replaced it mid-session; reloading`, {
        message: error instanceof Error ? error.message : String(error),
      });
      markReloaded();

      // Flush before the reload cancels it. Bounded, because a hung report must not strand the
      // user on a dead tab waiting for telemetry.
      await Promise.race([
        flushErrors().catch(() => {}),
        new Promise((resolve) => setTimeout(resolve, FLUSH_BUDGET_MS)),
      ]);

      if (typeof window !== 'undefined') window.location.reload();

      // The reload is not instantaneous; returning a promise that never settles leaves the
      // Suspense fallback up rather than flashing an error on a page that is going away.
      return new Promise<T>(() => {});
    }
  };
}

/**
 * Vite's own preload failures, which do not come through the import() rejection.
 *
 * `<link rel="modulepreload">` failing fires `vite:preloadError` on window instead, and left
 * alone it surfaces as a separate uncaught error with the same cause. Same decision, same guard.
 */
export function installChunkRecovery(target: Window): void {
  target.addEventListener('vite:preloadError', (event) => {
    /**
     * `payload` is a direct property of VitePreloadErrorEvent — NOT `detail.payload`, which is
     * what I wrote first and tsc refused. That refusal is the only reason this reads the right
     * field: a wrong read would quietly fall through to `event`, never match the stale-chunk
     * patterns, and leave the preload path silently unhandled.
     *
     * Widened rather than cast to the Vite type, because the projects here disagree about
     * whether `vite/client` augments WindowEventMap — one tsconfig sees VitePreloadErrorEvent,
     * another sees a bare Event, and a cast that satisfies one fails the other.
     */
    const error = (event as Event & { payload?: unknown }).payload ?? event;
    const online = typeof navigator === 'undefined' ? true : navigator.onLine !== false;
    if (!shouldReloadFor(error, online)) return;

    event.preventDefault?.();
    log.error('A module preload failed after a deploy; reloading', {
      message: error instanceof Error ? error.message : String(error),
    });
    markReloaded();
    void Promise.race([
      flushErrors().catch(() => {}),
      new Promise((resolve) => setTimeout(resolve, FLUSH_BUDGET_MS)),
    ]).then(() => target.location.reload());
  });
}
